import { LitElement, css, html, svg } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import { useEffect } from 'haunted';
import { clamp } from '../functions/clamp';

@customElement('ep-progress-stepper')
export class EPProgressStepper extends LitHauntedElement {

  @property({ type: Number, attribute: 'active-index', reflect: true })
  activeIndex: number = -1;

  @query('slot')
  slotElement?: HTMLSlotElement;

  get steps(): EPProgressStepperItem[] {
    return (this.slotElement?.assignedElements() ?? []).filter(e => e instanceof EPProgressStepperItem) as EPProgressStepperItem[];
  }

  public previous() {
    this.activeIndex = Math.min(this.steps.length, Math.max(-1, this.activeIndex - 1));
  }

  public next() {
    this.activeIndex = Math.min(this.steps.length, Math.max(-1, this.activeIndex + 1));
  }
  
  render() {
    useEffect(() => {
      // Intentionally allows `-1` and `length` because it should be possible to show 0 progress or all completed
      this.activeIndex = clamp(-1, this.activeIndex, this.steps.length);
    }, [this.activeIndex])
    return html`
      <div style="display: none;">
        <slot @slotchange=${() => this.requestUpdate()}></slot>
      </div>
      ${this.steps.map((item, index, { length }) => html`
        <div part="step" class="step">
          <span class="label" ?data-active=${this.activeIndex >= index} ?data-current=${this.activeIndex === index} ?data-completed=${this.activeIndex > index}>
            <span part="icon" class="icon-container">
              ${svg`
                <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24">
                  ${
                    this.activeIndex > index
                    ? svg`
                    <!-- Checkmark -->
                    <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm-2 17l-5-5 1.4-1.4 3.6 3.6 7.6-7.6L19 8l-9 9z"></path>
                    `
                    : svg`
                    <circle cx="12" cy="12" r="12"></circle>
                    <text x="12" y="12" text-anchor="middle" dominant-baseline="central">
                      ${index + 1}
                    </text>
                    `
                  }
                </svg>
              `}
            </span>
            <span part="label-container" class="label-container">
              <span part="label" class="text">${item.label}</span>
              <span part="caption" class="caption">${item.caption}</span>
            </span>
          </span>
        </div>
        ${
          index < length - 1
          ? html`
          <div part="connector" class="connector">
            <span part="line" class="line"></span>
          </div>
          `
          : ''
        }
      `)}
    `
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: row;
      align-items: center;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      font-family: var(--font-prose);
      --font-scale: 1.0;
    }

    .step {
      padding-left: 8px;
      padding-right: 8px;
    }

    .step .label {
      display: flex;
      align-items: center;
    }

    .step .label .icon-container {
      flex-shrink: 0;
      display: flex;
      padding-right: 8px;
    }

    svg {
      user-select: none;
      width: 1em;
      height: 1em;
      fill: currentColor;
      flex-shrink: 0;
      font-size: 1.5rem;
      display: block;
      transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
      color: rgba(0, 0, 0, 0.38);
    }

    [data-active] svg {
      color: #1976d2;
    }

    [data-completed] svg {
      opacity: 0.65;
      transform: scale(0.75);
    }

    svg text {
      fill: #fff;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .step .label .label-container {
      width: 100%;
      color: rgba(0, 0, 0, 0.6);
    }

    .step .label .label-container .text {
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.43;
      letter-spacing: 0.01071em;
      display: block;
      transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }

    .label[data-active] .label-container .text {
      color: rgba(0, 0, 0, 0.87);
      /* font-weight: 500; */
    }

    .label[data-current] .label-container .text {
      /* color: rgba(0, 0, 0, 0.87); */
      font-weight: 500;
    }

    .step .label .label-container .caption {
      margin: 0;
      font-weight: 400;
      font-size: 0.75rem;
      line-height: 1.66;
      letter-spacing: 0.03333em;
    }

    .connector {
      flex: 1 1 auto;
    }

    .connector .line {
      display: block;
      border-color: #bdbdbd;
      border-top-style: solid;
      border-top-width: 1px;
    }
  `
}

@customElement('ep-progress-stepper-item')
export class EPProgressStepperItem extends LitElement {

  @property({ reflect: true })
  label: string = '';

  @property({ reflect: true })
  caption?: string;

  render() {
    return html``;
  }

  static styles = css`
    :host {
      display: none;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-progress-stepper': EPProgressStepper,
    'ep-progress-stepper-item': EPProgressStepperItem,
  }
}
